/**
 * Opening a certificate without tripping the browser's pop-up blocker.
 *
 * A window.open() that runs after an HTTP response has lost the click that
 * asked for it, and browsers block it. Nothing appears, the blocked-pop-up
 * notice is easy to miss, and it reads to the user as being thrown back to
 * where they started.
 *
 * Which way round it depends on what is being opened:
 *
 *   downloadFile  the server sends the file as an attachment, so the page can
 *                 simply point at it. Downloading is not a pop-up, so nothing
 *                 is blocked and the page stays where it is.
 *   reserveTab    for a file whose address is only known once the server has
 *                 answered. Claim the tab while the click is still in hand,
 *                 then send it to the address when that arrives.
 *
 * A certificate whose address is known up front needs neither: give the user
 * a real link (`<base-button tag="a" :href="…" target="_blank">`), which they
 * open themselves and no browser blocks.
 */

/** Fetch a file the server sends as an attachment. Never blocked. */
export function downloadFile(url) {
  if (!url) return;
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  // No target: an attachment downloads without taking the page anywhere.
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Claim a tab now, while the click that asked for it is still ours. Call it
 * before the request goes out and hand what it returns to showInTab().
 * Returns null if the browser refused even this.
 */
export function reserveTab() {
  const tab = window.open('', '_blank');
  try {
    if (tab && tab.document && tab.document.body) {
      tab.document.title = 'Opening certificate…';
      tab.document.body.innerHTML =
        '<p style="font:16px/1.5 system-ui,sans-serif;padding:24px;color:#475569">'
        + 'Opening your certificate…</p>';
    }
  } catch (e) {
    // Some browsers hand back a tab that cannot be written to yet. It can
    // still be navigated, which is all that matters.
  }
  return tab || null;
}

/**
 * Send a reserved tab to the address. If there is no tab — the browser
 * refused it, or the user closed it while waiting — fetch the file instead,
 * so they are not left with nothing.
 */
export function showInTab(tab, url) {
  if (!url) {
    releaseTab(tab);
    return false;
  }
  if (tab && !tab.closed) {
    tab.location.href = url;
    return true;
  }
  downloadFile(url);
  return false;
}

/** Nothing to show after all — let a reserved tab go. */
export function releaseTab(tab) {
  if (tab && !tab.closed) tab.close();
}
